import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDataChannel, useLocalParticipant } from "@livekit/components-react";
import { Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { videoService, type ChatMessage } from "../api/video.api";

interface UIMessage extends ChatMessage {
  failed?: boolean;
  localId?: string;
}

interface InCallChatContentProps {
  /** Backend room UUID — required for /rooms/<id>/chat/. */
  roomId: string;
  /** LiveKit JWT for the backend's `X-LiveKit-Token` auth. */
  liveKitToken: string;
}

const MAX_CHARS = 2000;

/**
 * Chat slot rendered inside the SidePanel during a call.
 *
 * Receives over the LiveKit data channel (multiplexed with reactions etc. by
 * the `type` discriminator), sends + history-loads via the backend REST API.
 * Mirrors the behaviour of `internal/ChatPanel.tsx` but without the legacy
 * sliding/mobile-sheet chrome — SidePanel already provides the container.
 */
export function InCallChatContent({ roomId, liveKitToken }: InCallChatContentProps) {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const decoder = useMemo(() => new TextDecoder(), []);
  const { localParticipant } = useLocalParticipant();

  const addOrMerge = useCallback((incoming: UIMessage) => {
    setMessages((prev) => {
      if (incoming.seq > 0 && prev.some((m) => m.seq === incoming.seq)) return prev;
      const next = [...prev, incoming];
      next.sort((a, b) => {
        if (a.seq > 0 && b.seq > 0) return a.seq - b.seq;
        return a.timestamp.localeCompare(b.timestamp);
      });
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    videoService
      .getChatHistory(roomId, liveKitToken)
      .then((history) => {
        if (!cancelled) setMessages(history);
      })
      .catch(() => {
        // Soft-fail: never block the call UI on chat history.
      });
    return () => {
      cancelled = true;
    };
  }, [roomId, liveKitToken]);

  useDataChannel((msg) => {
    try {
      const parsed = JSON.parse(decoder.decode(msg.payload)) as {
        type?: string;
        message?: ChatMessage;
      };
      if (parsed.type === "chat" && parsed.message) {
        addOrMerge(parsed.message);
      }
    } catch {
      // non-chat payload — ignore
    }
  });

  // Keep the scroller pinned to the latest message.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const trimmed = draft.trim();
  const tooLong = draft.length > MAX_CHARS;
  const sendDisabled = !trimmed || tooLong || sending;

  const handleSend = useCallback(async () => {
    if (sendDisabled) return;
    const text = draft;
    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setDraft("");
    setSending(true);
    try {
      const saved = await videoService.sendChatMessage(roomId, text, liveKitToken);
      addOrMerge(saved);
    } catch {
      addOrMerge({
        seq: -Date.now(),
        timestamp: new Date().toISOString(),
        sender_name: "You",
        sender_id: "",
        text,
        failed: true,
        localId,
      });
    } finally {
      setSending(false);
    }
  }, [addOrMerge, draft, roomId, liveKitToken, sendDisabled]);

  return (
    <div className="flex flex-col h-full">
      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto no-scrollbar p-4 space-y-3 text-sm">
        {messages.length === 0 && (
          <p className="text-xs text-muted-foreground">{t("call.chat.empty", { defaultValue: "No messages yet." })}</p>
        )}
        {messages.map((m) => {
          const isOwn = Boolean(localParticipant?.identity) && m.sender_id === localParticipant?.identity;
          return (
            <div
              key={m.seq > 0 ? `seq-${m.seq}` : m.localId ?? `ts-${m.timestamp}`}
              className={`flex flex-col ${isOwn ? "items-end" : "items-start"}`}
            >
              {!isOwn && (
                <span className="text-[11px] font-bold text-muted-foreground mb-0.5 px-1">{m.sender_name}</span>
              )}
              <div
                className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-snug whitespace-pre-wrap break-words ${
                  isOwn
                    ? "bg-primary text-primary-foreground rounded-br-md"
                    : "bg-muted text-foreground rounded-bl-md"
                }`}
              >
                {m.text}
              </div>
              <span className="mt-0.5 px-1 text-[10px] text-muted-foreground">
                {new Date(m.timestamp).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" })}
                {m.failed && (
                  <span className="ms-1.5 text-red-500">{t("call.chat.failed", { defaultValue: "failed to send" })}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
      <div className="relative shrink-0 border-t border-border p-3 flex items-center gap-2">
        {tooLong && (
          <p className="absolute bottom-full mb-1 start-3 text-xs text-red-500">
            {t("call.chat.tooLong", {
              defaultValue: "Message too long ({{length}}/{{max}})",
              length: draft.length,
              max: MAX_CHARS,
            })}
          </p>
        )}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleSend();
            }
          }}
          placeholder={t("call.chat.placeholder", { defaultValue: "Message everyone…" })}
          className="flex-1 h-10 px-3 bg-muted/50 border border-border rounded-xl text-sm outline-none focus:border-primary transition-colors text-foreground"
          aria-label={t("call.chat.messageAriaLabel", { defaultValue: "Chat message" })}
        />
        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={sendDisabled}
          aria-label={
            sending
              ? t("call.chat.sending", { defaultValue: "Sending…" })
              : t("call.chat.send", { defaultValue: "Send" })
          }
          className={`size-10 rounded-xl flex items-center justify-center transition-colors shrink-0 ${
            sendDisabled ? "bg-muted text-muted-foreground/50" : "bg-primary text-primary-foreground hover:opacity-90"
          }`}
        >
          <Send className="size-4" />
        </button>
      </div>
    </div>
  );
}
